import { definePageSections, defineSection } from "../sections.js";
import type {
  ClosingContent,
  FaqContent,
  FeaturedBoxesContent,
  HeroContent,
  HomeBattlesContent,
  HomeContent,
  HomeForgeContent,
  LiveHitsContent,
  ProductsContent,
  PromisesContent,
  PromoCardsContent,
  RecentOpensContent,
  ReviewsContent,
} from "./homeContent.js";
import { HomeBattles } from "./sections/battles/HomeBattles.js";
import { HomeClosing } from "./sections/closing/HomeClosing.js";
import { HomeFaq } from "./sections/faq/HomeFaq.js";
import { HomeFeaturedBoxes } from "./sections/featured-boxes/HomeFeaturedBoxes.js";
import { HomeForge } from "./sections/forge/HomeForge.js";
import { HomeHero } from "./sections/hero/HomeHero.js";
import { HomeLiveHits } from "./sections/live-hits/HomeLiveHits.js";
import { HomeProducts } from "./sections/products/HomeProducts.js";
import { HomePromises } from "./sections/promises/HomePromises.js";
import { HomePromoCards } from "./sections/promo-cards/HomePromoCards.js";
import { HomeRecentOpens } from "./sections/recent-opens/HomeRecentOpens.js";
import { HomeReviews } from "./sections/reviews/HomeReviews.js";

/**
 * Every section of Home, with the variants each is drawn in and the settings it takes. The store's
 * banners are the hero's: while any are live, every variant of it shows them. This is the one
 * list Home's templates pick from (`drawSections` in `registry.ts`), the stories draw every
 * variant of, and a store's page will. A new variant is a value of its component's `variant`
 * prop plus an entry here; `homeSections.test.tsx` renders every one.
 */
export const HOME_SECTIONS = definePageSections<HomeContent>()({
  hero: defineSection<HeroContent>()({
    label: "Hero",
    Component: HomeHero,
    variants: {
      split: {
        label: "Split",
        description: "Headline and actions beside the art, on the stage under the nav.",
      },
      centered: {
        label: "Centered",
        description: "Headline and actions centered over the art, on the stage under the nav.",
      },
      carousel: {
        label: "Carousel",
        description:
          "Headline and actions as a carousel slide, the same frame the store's banners show in.",
      },
    },
    defaultVariant: "split",
    settings: {
      shine: {
        label: "Shine",
        description: "Sweep a light across the carousel's calls to action as the pointer arrives.",
        default: false,
      },
    },
  }),
  liveHits: defineSection<LiveHitsContent>()({
    label: "Live hits",
    Component: HomeLiveHits,
    variants: {
      strip: {
        label: "Strip",
        description: "A glass strip of recent big pulls, pulled up into the hero's edge.",
      },
      cards: {
        label: "Cards",
        description: "Each pull a card in its rarity, with who pulled it, on a rail.",
      },
    },
    defaultVariant: "strip",
    settings: {},
  }),
  recentOpens: defineSection<RecentOpensContent>()({
    label: "Recent opens",
    Component: HomeRecentOpens,
    variants: {
      compact: { label: "Compact", description: "The shopper's best pulls as small cards." },
      cards: {
        label: "Cards",
        description: "The shopper's best pulls as cards in their rarity, on a rail.",
      },
    },
    defaultVariant: "compact",
    settings: {},
  }),
  featuredBoxes: defineSection<FeaturedBoxesContent>()({
    label: "Featured boxes",
    Component: HomeFeaturedBoxes,
    variants: {
      grid: { label: "Grid", description: "A grid of box cards, many to a row." },
      large: {
        label: "Large",
        description: "Large box cards: one to a row on phones, up to three on a wide screen.",
      },
      rail: { label: "Rail", description: "Box cards on a rail that scrolls sideways." },
    },
    defaultVariant: "grid",
    settings: {},
  }),
  battles: defineSection<HomeBattlesContent>()({
    label: "Battles",
    Component: HomeBattles,
    variants: {
      grid: { label: "Grid", description: "Battle cards, up to three across." },
      rows: { label: "Rows", description: "One wide line per battle, down the page." },
    },
    defaultVariant: "grid",
    settings: {},
  }),
  forge: defineSection<HomeForgeContent>()({
    label: "Forge",
    Component: HomeForge,
    variants: {
      showcase: {
        label: "Showcase",
        description:
          "The pitch over a rail of the boxes shoppers built, each with who built it; the band while none are built.",
      },
      band: { label: "Band", description: "The pitch centered in a lit band, alone." },
      stage: {
        label: "Stage",
        description: "The pitch at the start, the store's boxes turning behind it.",
      },
    },
    defaultVariant: "showcase",
    settings: {},
  }),
  promoCards: defineSection<PromoCardsContent>()({
    label: "Promo cards",
    Component: HomePromoCards,
    variants: {
      row: { label: "Row", description: "Card-wide links in one row, stacked on phones." },
    },
    defaultVariant: "row",
    settings: {},
  }),
  products: defineSection<ProductsContent>()({
    label: "Products",
    Component: HomeProducts,
    variants: {
      grid: { label: "Grid", description: "Product cards, up to six across." },
    },
    defaultVariant: "grid",
    settings: {},
  }),
  reviews: defineSection<ReviewsContent>()({
    label: "Reviews",
    Component: HomeReviews,
    variants: {
      grid: { label: "Grid", description: "Review cards, up to four across." },
    },
    defaultVariant: "grid",
    settings: {},
  }),
  promises: defineSection<PromisesContent>()({
    label: "Promises",
    Component: HomePromises,
    variants: {
      grid: { label: "Grid", description: "The store's promises as feature cards." },
    },
    defaultVariant: "grid",
    settings: {},
  }),
  faq: defineSection<FaqContent>()({
    label: "FAQ",
    Component: HomeFaq,
    variants: {
      split: { label: "Split", description: "The heading beside the questions." },
      centered: { label: "Centered", description: "Heading and questions in a narrow column." },
      stacked: { label: "Stacked", description: "The heading over the questions, full width." },
    },
    defaultVariant: "split",
    settings: {},
  }),
  closing: defineSection<ClosingContent>()({
    label: "Closing band",
    Component: HomeClosing,
    variants: {
      band: { label: "Band", description: "The page's last word and action in a lit band." },
    },
    defaultVariant: "band",
    settings: {},
  }),
});
